import React, { useEffect, useState } from "react";
import AdminLayout from "../layout/AdminLayout";
import Header from "../components/Header";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdminPricing, updateAdminPricing, clearPricingMessages } from "../redux/reducers/pricingSlice";
import { FiDollarSign, FiSave } from "react-icons/fi";

const PricingManagement = () => {
  const dispatch = useDispatch();
  const { pricing, loading, saving, error, successMessage } = useSelector((state) => state.pricing);

  const [monthly, setMonthly] = useState("");
  const [yearly, setYearly] = useState("");

  useEffect(() => {
    dispatch(fetchAdminPricing());
  }, [dispatch]);

  useEffect(() => {
    if (pricing) {
      setMonthly(pricing.monthlyPrice);
      setYearly(pricing.yearlyPrice);
    }
  }, [pricing]);

  useEffect(() => {
    if (successMessage || error) {
      const t = setTimeout(() => dispatch(clearPricingMessages()), 5000);
      return () => clearTimeout(t);
    }
  }, [successMessage, error, dispatch]);

  const handleSave = (e) => {
    e.preventDefault();
    if (!monthly || !yearly) return;
    dispatch(updateAdminPricing({ monthlyPrice: Number(monthly), yearlyPrice: Number(yearly) }));
  };

  const yearlySaving = monthly && yearly ? (monthly * 12 - yearly).toFixed(0) : 0;

  return (
    <AdminLayout>
      <div className="p-4">
        <Header
          title="Pricing Management"
          subTitle="Admin"
          description="Update subscription pricing. Changes reflect on the landing page and all users are notified by email."
        />

        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm">
            ✅ {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Edit Pricing */}
          <fieldset className="border-2 rounded-2xl px-6 py-6">
            <legend className="font-bold px-1">Update Pricing</legend>
            {loading ? (
              <p className="text-gray-400 text-sm py-4">Loading current pricing...</p>
            ) : (
              <form onSubmit={handleSave} className="space-y-5 mt-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Monthly Price (USD)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      value={monthly}
                      onChange={(e) => setMonthly(e.target.value)}
                      required
                      min="1"
                      placeholder="149"
                      className="w-full border-2 rounded-xl pl-7 pr-4 py-2.5 text-sm focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <p className="text-gray-400 text-xs mt-1">Charged per month for monthly subscribers</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Yearly Price (USD)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      value={yearly}
                      onChange={(e) => setYearly(e.target.value)}
                      required
                      min="1"
                      placeholder="1490"
                      className="w-full border-2 rounded-xl pl-7 pr-4 py-2.5 text-sm focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <p className="text-gray-400 text-xs mt-1">Charged once per year for yearly subscribers</p>
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-semibold"
                >
                  <FiSave />
                  {saving ? "Saving & Notifying Users..." : "Save Pricing"}
                </button>
              </form>
            )}
          </fieldset>

          {/* Preview */}
          <fieldset className="border-2 rounded-2xl px-6 py-6">
            <legend className="font-bold px-1">Live Preview</legend>
            <p className="text-gray-400 text-xs mb-4 mt-2">This is how pricing appears on the landing page</p>

            <div className="border-2 border-purple-500 rounded-2xl p-6 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Full Access</span>
              </div>
              <div className="text-center mb-4">
                <p className="text-gray-400 text-xs mb-1">Monthly billing</p>
                <div className="flex items-end justify-center gap-1">
                  <span className="text-4xl font-bold text-gray-900">${monthly || "—"}</span>
                  <span className="text-gray-400 mb-1 text-sm">/mo</span>
                </div>
              </div>
              <div className="text-center border-t pt-4 mt-2">
                <p className="text-gray-400 text-xs mb-1">Yearly billing</p>
                <div className="flex items-end justify-center gap-1">
                  <span className="text-4xl font-bold text-gray-900">${yearly ? (yearly / 12).toFixed(0) : "—"}</span>
                  <span className="text-gray-400 mb-1 text-sm">/mo</span>
                </div>
                {yearlySaving > 0 && (
                  <p className="text-green-600 text-xs mt-1">Saves ${yearlySaving}/year vs monthly</p>
                )}
                <p className="text-gray-400 text-xs">${yearly || "—"}/year billed annually</p>
              </div>
            </div>

            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-800">
              <strong>Note:</strong> Saving will immediately email all active users about the price change.
            </div>
          </fieldset>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PricingManagement;
